import React, { ReactElement, useEffect, useState } from 'react';
import FormSelector from 'adapter/in/common/components/Selector';
import { getErrorMessage, onChangeSelect, onChangeSelectAll, SelectorContainerProps } from 'adapter/in/common/form/Selector';
import { FieldStatus, RulesRHF, SelectorFieldDataImpl, SelectorOption, SelectorType } from 'adapter/in/common/form/types';
import {
    buildSelectorFieldData,
    isSelectorFieldDataValid,
    useBaseSelectorContainer,
} from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import i18n from 'asset/translation';
import { useBaseDashboardsApiSelector } from 'adapter/in/common/hooks/form/useBaseDashboardsApiSelector';
import { dispatchFormChangesView } from 'adapter/in/common/context/formChanges';

const DEVICE_SELECTOR_MAX_TAG_COUNT = 20;
const isDeviceSelector = (name: string) => /^device_id(?:-\d+)?$/.test(name);

export const ApiSelectorContainer = ({
    name,
    label,
    configType,
    dataDependsOn,
    affectsTo,
    disabled,
    pageSize,
    footer,
}: SelectorContainerProps): ReactElement => {
    const [rules, setRules] = useState<RulesRHF>({
        required: i18n.t('widgets.errorNotEmpty').toString(),
        validate: (v: SelectorFieldDataImpl) => isSelectorFieldDataValid(1, 100)(v),
    });
    const { value, onChange, invalid, error, shouldLoad, dependantValues, register, trigger } = useBaseSelectorContainer(
        name,
        rules,
        undefined,
        dataDependsOn,
        affectsTo
    );
    const { setHasChange: setWidgetMenuHasChange } = dispatchFormChangesView();
    const { isLoading, maxSelectionLength, minSelectionLength, options } = useBaseDashboardsApiSelector<SelectorOption>(
        name,
        SelectorType.SELECT,
        configType,
        pageSize ?? 0,
        value,
        shouldLoad,
        dependantValues,
        (updatedValue) => {
            onChange(updatedValue);
        }
    );
    useEffect(() => {
        const rulesToSet = {
            required: minSelectionLength > 0 ? i18n.t('widgets.errorNotEmpty').toString() : false,
            validate: (v: SelectorFieldDataImpl) => isSelectorFieldDataValid(minSelectionLength, maxSelectionLength)(v),
        };
        setRules(rulesToSet);
        register(name, rulesToSet);
        trigger(name);
    }, [minSelectionLength, maxSelectionLength]);

    const handleClear = (onChangeCallback: (e: SelectorFieldDataImpl) => void) => {
        onChangeCallback(buildSelectorFieldData(FieldStatus.HAS_VALUE, value.initialValue, configType, []));
    };
    const hasError =
        (invalid && !!error && error.type !== 'loading' && options && options.length > 0) ||
        !isSelectorFieldDataValid(minSelectionLength, maxSelectionLength)(value);
    return (
        <FormSelector
            isLoading={isLoading}
            name={name}
            label={label}
            placeholder={
                minSelectionLength === 0 ? i18n.t('common.selector.placeholder.optional') : i18n.t('common.selector.placeholder.required')
            }
            disabled={disabled}
            maxSelectionLength={maxSelectionLength}
            minSelectionLength={minSelectionLength}
            value={value?.value}
            error={hasError}
            options={options}
            onChange={(selectedValue: string | string[]) => {
                setWidgetMenuHasChange(true);
                onChangeSelect(minSelectionLength, maxSelectionLength, configType, value)(selectedValue, onChange);
            }}
            onClear={() => handleClear(onChange)}
            errorMessage={hasError ? getErrorMessage(value.value, minSelectionLength, maxSelectionLength) : ''}
            handleButtonSelectAll={(values: string[]) => onChangeSelectAll(configType)(values, onChange)}
            handleClearAll={() => handleClear(onChange)}
            footer={footer}
            maxTagCount={isDeviceSelector(name) ? DEVICE_SELECTOR_MAX_TAG_COUNT : undefined}
        />
    );
};
